import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { List } from 'immutable';
import { concat, Observable, of } from 'rxjs';
import { catchError, flatMap, map, switchMap, take } from 'rxjs/operators';
import { v4 } from 'uuid';
import { ActionsEnum as AuthActionsEnum } from '../auth-data/auth-data.actions';
import {
  ActionsEnum,
  FetchFeaturesData,
  fetchFeaturesData,
  FetchFeaturesDataError,
  fetchFeaturesDataError,
  FetchFeaturesDataSuccess,
  fetchFeaturesDataSuccess,
  UpdateFeaturesData,
  updateFeaturesData,
  updateQuotasBannersCanvases,
} from './features-data.actions';
import { FeaturesDataService } from './features-data.service';
import { ProvisioningFeatures } from './provisioning-features.model';
import { AccountQuota } from './sub-models/account-quota.model';
import { Banner } from './sub-models/banner.model';
import { Canvas } from './sub-models/canvas.model';

@Injectable()
export class FeaturesDataEffects {
  constructor(
    private _actions: Actions,
    private _featuresDataService: FeaturesDataService
  ) {}

  @Effect()
  onAuthenticated: Observable<FetchFeaturesData> = this._actions.pipe(
    ofType(AuthActionsEnum.AUTHENTICATED),
    take(1),
    map(() => fetchFeaturesData(v4()))
  );

  @Effect()
  fetchQuotasBannersCanvases: Observable<any> = this._actions.pipe(
    ofType(ActionsEnum.FETCH_QUOTAS_BANNERS_CANVASES),
    switchMap(() => this._featuresDataService.getQuotaBannersCanvases()),
    map((quotasBannersCanvases: any[]) =>
      updateQuotasBannersCanvases(
        quotasBannersCanvases[0] as List<AccountQuota>,
        quotasBannersCanvases[1] as List<Banner>,
        quotasBannersCanvases[2] as List<Canvas>
      )
    )
  );

  @Effect()
  fetchFeaturesData: Observable<
    FetchFeaturesDataSuccess | FetchFeaturesDataError | UpdateFeaturesData
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_FEATURES_DATA),
    switchMap(({ payload }: FetchFeaturesData) => {
      const { dispatchId } = payload;

      return this._featuresDataService.getFeaturesData().pipe(
        flatMap((featuresData: ProvisioningFeatures) =>
          concat([
            updateFeaturesData(featuresData),
            fetchFeaturesDataSuccess(dispatchId, featuresData),
          ])
        ),
        catchError((error) => of(fetchFeaturesDataError(dispatchId, error)))
      );
    })
  );
}
